import { useState, useEffect } from 'react';

/**
 * Hook to detect online/offline status
 * Listens to browser online/offline events and Supabase connectivity
 */
export function useOnlineStatus() {
  const [isOnline, setIsOnline] = useState(() => {
    // Check initial state
    if (typeof navigator !== 'undefined') {
      return navigator.onLine;
    }
    return true; // Assume online if navigator not available
  });

  const [isSupabaseOnline, setIsSupabaseOnline] = useState(true);
  const [lastChecked, setLastChecked] = useState<Date | null>(null);

  useEffect(() => {
    // Listen to browser online/offline events
    const handleOnline = () => {
      setIsOnline(true);
      checkSupabaseConnectivity();
    };

    const handleOffline = () => {
      setIsOnline(false);
      setIsSupabaseOnline(false);
    };

    // Check Supabase connectivity periodically
    const checkConnectivity = async () => {
      await checkSupabaseConnectivity();
    };

    // Initial check
    checkSupabaseConnectivity();

    // Set up periodic checks (every 30 seconds)
    const connectivityInterval = setInterval(checkConnectivity, 30000);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(connectivityInterval);
    };
  }, []);

  const checkSupabaseConnectivity = async () => {
    try {
      const supabaseUrl = (import.meta.env as any).VITE_SUPABASE_URL;
      if (!supabaseUrl) {
        setIsSupabaseOnline(false);
        return;
      }

      // Try a lightweight HEAD request to Supabase
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout

      const anonKey = (import.meta.env as any).VITE_SUPABASE_ANON_KEY || '';
      const base = String(supabaseUrl).replace(/\/$/, '');

      // Prefer GoTrue /auth/v1/health (usually 200). If missing (404), fall back to REST OpenAPI doc.
      let response = await fetch(`${base}/auth/v1/health`, {
        method: 'GET',
        signal: controller.signal,
        headers: {
          apikey: anonKey,
        },
      });

      if (response.status === 404) {
        response = await fetch(`${base}/rest/v1/`, {
          method: 'GET',
          signal: controller.signal,
          headers: {
            apikey: anonKey,
            Authorization: `Bearer ${anonKey}`,
            Accept: 'application/openapi+json',
          },
        });
      }

      clearTimeout(timeoutId);

      // Any HTTP status from the project means the host answered (including 401).
      setIsSupabaseOnline(response.status > 0 && response.status < 600);
      setLastChecked(new Date());
    } catch (error) {
      setIsSupabaseOnline(false);
      setLastChecked(new Date());
    }
  };

  return {
    isOnline,
    isSupabaseOnline,
    isFullyOffline: !isOnline || !isSupabaseOnline,
    lastChecked,
    checkConnectivity: checkSupabaseConnectivity,
  };
}


